/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { replace } from 'lodash';
import { useMemo, useCallback } from 'react';
import { styled } from 'styled-components';
import { CheckboxGroup as MotiliCheckboxGroup } from '@motili/ui';
import { DisplayType } from 'common/types';

type CheckboxGroupProps = {
    name: string;
    label: string;
    value: string[];
    options: DisplayType[];
    onChange: (value: string[]) => void;
    className?: string;
    required?: boolean;
    dataTestId?: string;
};
export default function CheckboxGroup(props: CheckboxGroupProps) {
    const { name, label, value, options, onChange, required, dataTestId } =
        props;
    const chkOptions = useMemo(() => {
        return options.map(option => ({
            checked: value.includes(option.id),
            value: option.id,
            label: option.display,
            id: `${name}-${option.id}`,
        }));
    }, [options, value]);
    const handleChange = useCallback(
        (e: any) => {
            const oriId = replace(e.target.value, `${name}-`, '');
            if (e.target.checked) {
                onChange([...value, oriId]);
            } else {
                onChange(value.filter(id => id !== oriId));
            }
        },
        [value]
    );
    return (
        <StyledCheckboxGroup
            className={props.className || ''}
            data-test-id={dataTestId}
        >
            <MotiliCheckboxGroup
                name={name}
                label={label}
                onChange={handleChange}
                options={chkOptions}
                required={required}
            />
        </StyledCheckboxGroup>
    );
}

const StyledCheckboxGroup = styled.div`
    > span {
        margin: 5px 0;
        display: block;
        label {
            font-weight: 400;
            font-size: 14px;
            line-height: 19px;
            color: #333;
        }
        label::before {
            width: 20px;
            height: 20px;
            border-color: #cccccc;
        }
        label::after {
            width: 3px;
            height: 3px;
        }
    }
    br {
        display: none;
    }
`;
